import { FunctionComponent } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
	faBookOpen,
	faLifeRing,
	faUpDownLeftRight,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { Icon } from "@/components/Icon/Icon";
import { ROUTE_CONFIG } from "@/routes/config";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { HelpChat } from "./HelpChat";
import { useHelpContext } from "./helpContext";
import { useHelpPanelResize } from "./useHelpPanelResize";

/**
 * The public help center. Cited articles carry absolute URLs from the assistant, so this is only
 * needed for the "browse the help center" affordance.
 */
const HELP_CENTER_URL = "https://help.signalist.io";

interface HelpPanelProps {
	isOpen: boolean;
	onClose: () => void;
}

/**
 * The floating help widget: an AI chat answered from the published help center. It docks
 * bottom-right like a regular chat widget (no backdrop — the page stays interactive). Articles
 * live on the help center itself, so every article reference here opens there in a new tab.
 */
export const HelpPanel: FunctionComponent<HelpPanelProps> = ({ isOpen, onClose }) => {
	const { t } = useTranslation();

	const helpContext = useHelpContext();

	// The ticket list only exists for a company with SUPPORT_TICKETS (PREC-3937); the chat's own
	// "send to support" below is not gated and keeps working for everyone.
	const isSupportActive = useIsFeatureActive(FeaturesCodes.SUPPORT_TICKETS);

	// The panel can be dragged larger from its top-left corner; today's size is the minimum.
	const { size, isResizing, onResizeStart } = useHelpPanelResize();

	const pageContext = { routePattern: helpContext.routePattern, label: helpContext.label };

	// Starter chips are fixed copy: they used to come from the article corpus, which this app no
	// longer holds — the help center is a site now, and its questions are not fetched here. A
	// missing key returns the key itself rather than an array, so the chips are dropped instead of
	// crashing the panel.
	const suggestionCopy = t("helpCenter.chat.suggestions", { returnObjects: true });
	const suggestions = Array.isArray(suggestionCopy) ? (suggestionCopy as string[]) : [];

	return ReactDOM.createPortal(
		<div
			className={classNames(
				"fixed bottom-4 right-4 z-[9999] flex max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-border-light bg-white shadow-2xl ease-in-out",
				// Suppress the open/close transition while dragging so the panel tracks the cursor 1:1.
				isResizing ? "" : "transition-all duration-300",
				isOpen
					? "pointer-events-auto translate-y-0 opacity-100"
					: "pointer-events-none translate-y-3 opacity-0"
			)}
			style={{ width: size.width, height: size.height }}
		>
			<button
				aria-label={t("helpCenter.resize")}
				className="absolute left-0 top-0 z-10 flex h-5 w-5 cursor-nwse-resize items-center justify-center text-text-meta/50 transition-colors duration-150 hover:text-text-meta"
				onPointerDown={onResizeStart}
				type="button"
			>
				<Icon icon={faUpDownLeftRight} className="text-[10px]" />
			</button>

			<div className="flex items-center justify-between border-b border-border-light px-4 py-3">
				<h2 className="text-base font-semibold text-text">{t("helpCenter.title")}</h2>
				<div className="flex items-center gap-1">
					{isSupportActive && (
						<Link
							className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-blue-action transition-colors duration-150 hover:bg-blue-action-200"
							to={ROUTE_CONFIG.SUPPORT}
							onClick={onClose}
						>
							<Icon icon={faLifeRing} />
							{t("support.myTickets")}
						</Link>
					)}
					<a
						className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-blue-action transition-colors duration-150 hover:bg-blue-action-200"
						href={HELP_CENTER_URL}
						rel="noopener noreferrer"
						target="_blank"
					>
						<Icon icon={faBookOpen} />
						{t("helpCenter.openFull")}
					</a>
					<button
						aria-label={t("helpCenter.close")}
						className="cursor-pointer p-1 text-text-meta hover:text-text"
						onClick={onClose}
					>
						<Icon icon={faXmark} />
					</button>
				</div>
			</div>

			<HelpChat pageContext={pageContext} suggestions={suggestions} />
		</div>,
		document.body
	);
};
